import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { ProductImageStyles, productImageStyles } from "@insite/content-library/Components/ProductImage";
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { ProductSelectionPartsModel } from "@tCooneyBrothers/ApiModels";
import * as React from "react";
import { ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    products: ProductSelectionPartsModel[];
    parentProduct: string;
}

type Props = OwnProps;

export interface SparePartImageStyles {
    productImageStyles?: ProductImageStyles;
    headerText?: TypographyPresentationProps;
    container?: GridContainerProps;
    imageGridItem?: GridItemProps;
    image?: LazyImageProps;
    titleLink?: LinkPresentationProps;
    mainContainer?: InjectableCss;
    indexNumber?: InjectableCss;
    subHeaderText?: InjectableCss;
    description?: InjectableCss;
    textContainer?: InjectableCss;
    seeChatText?: InjectableCss;
    hoverText?: InjectableCss;
}

productImageStyles.linkWrappingImage = {
    css: css`
        background: #fff;
        justify-content: center;
        div {
            width: 100%;
        }
        p[class^="TypographyStyle"] {
            max-height: 150px;
            overflow: hidden;
            font-size: 12px;
        }
    `,
};

export const sparePartImageStyles: SparePartImageStyles = {
    headerText: {
        weight: "bold",
        css: css`
            text-align: left;
            margin: 10px 0 5px 0;
            font-weight: 700;
            margin-bottom: 1rem;
            display: block;
            font-size: 20px;
            line-height: 28px;
        `,
    },
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: 5px 0;
            cursor: pointer;
        `,
    },
    imageGridItem: {
        width: 3,
        css: css`
            width: 50%;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                        `,
                        css`
                            width: 100%;
                        `,
                        css`
                            width: 100%;
                        `,
                    ],
                    "max",
                );
            }}
        `,
    },
    image: {
        // width: "100%",
        css: css`
            img {
                height: 100%;
            }
        `,
    },
    titleLink: {
        typographyProps: {
            ellipsis: false,
            css: css`
                width: 100%;
                text-align: left;
                margin: 0;
            `,
        },
        css: css`
            width: 270px;
            span {
                color: #474747 !important;
            }
        `,
    },
    mainContainer: {
        css: css`
            display: flex;
            max-width: 80%;
            margin: 25px auto;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                        `,
                        css`
                            flex-direction: column;
                        `,
                        css`
                            max-width: 100%;
                            flex-direction: column;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    textContainer: {
        css: css`
            padding-left: 20px;
        `,
    },
    seeChatText: {
        css: css`
            margin-top: 20px;
        `,
    },
    hoverText: {
        css: css`
            margin-bottom: 20px;
        `,
    },
    indexNumber: {
        css: css`
            padding-right: 7px;
        `,
    },
    subHeaderText: {
        css: css`
            padding-bottom: 10px;
        `,
    },
    description: {
        css: css`
            padding: 4px;
        `,
    },
};
const styles = sparePartImageStyles;

const SparePartImage: React.FC<Props> = ({ products, parentProduct }) => {
    const [productImage, setProductImage] = React.useState(
        (products && products.length > 0 && products[0].partImage) || "",
    );

    const displayImageHandler = (product: ProductSelectionPartsModel): void => {
        setProductImage(product.partImage);
    };

    return (
        <>
            <StyledWrapper {...styles.mainContainer}>
                <StyledWrapper {...styles.imageGridItem}>
                    <StyledWrapper {...styles.container}>
                        <LazyImage {...styles.image} src={productImage} />
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.textContainer}>
                    <StyledWrapper {...styles.hoverText}>
                        Hover over a list item below to see included parts represented in the image to the left.
                    </StyledWrapper>
                    {products.map(product => (
                        <StyledWrapper key={product.id!} {...styles.description}>
                            <Link {...styles.titleLink} onMouseEnter={e => displayImageHandler(product)}>
                                {`${product.partNumber}. ${product.pictureDescription}`}
                            </Link>
                        </StyledWrapper>
                    ))}
                    <StyledWrapper {...styles.seeChatText}>
                        See the chart below for details on each Spirax Sarco {parentProduct} replacement part, including
                        images, desciption, and links to see full specs and buy online.
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

export default SparePartImage;
